import Avatar from "@components/avatar/Avatar";
import { IconButton } from "@components/button";
import { withTheme } from "@hoc/withTheme";
import { ICompany } from "@odata/EntityTypes";
import { AccountingCode, CompanyStateCode } from "@odata/GeneratedEnums";
import { getCompanyLogoUrl, hasPayrollEnabled } from "@utils/CompanyUtils";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { CustomerPortalIcon, IProps as IIconProps } from "../../components/icon";
import { AvatarSize, IconSize } from "../../enums";
import { KeyName } from "../../keyName";
import { ROUTE_SWITCH_TO_CUSTOMER_PORTAL } from "../../routes";
import TestIds from "../../testIds";
import { PropsWithTheme } from "../../theme";
import {
    BackgroundMask,
    Body,
    BoldLabel,
    CompanyActionWrapper,
    CompanyTileAndActionWrapper,
    CompanyTileAvatarButton,
    Content,
    DeleteRequestedText,
    Footer,
    Header,
    LegalNumber,
    StyledCompanyTile
} from "./CompanyDashboard.styles";


export interface ICompanyTileProps {
    company: ICompany;
    // color: keyof DefaultTheme;
    isSelected: boolean;
    isDisabled?: boolean;
    isDeleteRequested?: boolean;
    onClick: (id: number, ref: React.RefObject<HTMLElement>) => void;

    // action handling
    /** "Active" action that can be clicked on. */
    actionIcon: React.ComponentType<IIconProps>;
    /** black icon that represents current state the company tile is in (archived). Cannot be used together with actionIcon. */
    statusIcon: React.ComponentType<IIconProps>;
    /** Used for both actionIcon and statusIcon - only one of them can be used at one time. */
    iconTitle?: string;

    onActionClick: (id: number, event: React.MouseEvent) => void;
}

class CompanyTile extends React.PureComponent<ICompanyTileProps & WithTranslation & PropsWithTheme> {
    private _ref = React.createRef<HTMLInputElement>();

    handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.target === e.currentTarget && // don't steal clicks from inner button
            (e.key === KeyName.Enter || e.key === KeyName.Space)) {
            e.preventDefault();
            this.props.onClick?.(this.props.company.Id, this._ref);
        }
    };

    handleSwitchToCustomerPortal = (event: React.MouseEvent) => {
        // we switch to customer portal using router navigation - prevent duplicate
        // handling of the click on the whole CompanyTile
        event.stopPropagation();
    };

    handleActionClick = (event: React.MouseEvent) => {
        this.props.onActionClick?.(this.props.company.Id, event);
    };

    renderActionIcon = (): React.ReactElement => {
        const Icon = this.props.actionIcon ?? this.props.statusIcon;

        if (!Icon) {
            return null;
        }

        const title = this.props.iconTitle;
        const icClickable = !!this.props.actionIcon;
        let content = <Icon width={IconSize.S}
                            color={icClickable ? null : "C_ICON_done"}
                            title={icClickable ? null : title}
                            preventHover={!icClickable}/>;

        if (icClickable) {
            content = (
                <IconButton onClick={this.handleActionClick}
                            title={title}
                            isDecorative>
                    {content}
                </IconButton>
            );
        }

        content = (
            <CompanyActionWrapper>
                {content}
            </CompanyActionWrapper>
        );

        return content;
    };

    render() {
        const { company } = this.props;
        const { Id } = company;
        const logoUrl = getCompanyLogoUrl(company);
        const isNonProfit = company.Accounting?.Code === AccountingCode.AccountingForNonprofitOrganizations;
        let footerText = isNonProfit ? this.props.t("Companies:CompanyTile.NonProfit") : `${company.Accounting?.Name ?? ""}`;
        const hasPayrollModule = hasPayrollEnabled(company);
        if (hasPayrollModule) {
            footerText += `\n${this.props.t("Common:Modules.Payroll")}`;
        }
        const isInitialized = company.StateCode !== CompanyStateCode.New;
        const isArchived = company.StateCode === CompanyStateCode.Archived;
        const isDeleteRequested = this.props.isDeleteRequested;

        const isGray = !isInitialized || isDeleteRequested || isArchived;

        const showCustomerPortalButton = isInitialized && company.CanAccessCustomerPortal;
        const isTileClickable = !this.props.actionIcon && !this.props.isDisabled && !isDeleteRequested;

        return (
            // use extra wrapper so that we can easily use reduced opacity for the whole tile, when being removed,
            // but keep the action icon at full opacity
            <CompanyTileAndActionWrapper data-companyid={Id}
                                         data-testid={TestIds.CompanyTile}>
                {this.renderActionIcon()}
                <StyledCompanyTile
                    ref={this._ref}
                    tabIndex={0}
                    onKeyDown={this.handleKeyDown}
                    onClick={this.props.onClick.bind(this, Id, this._ref)}
                    $isSelected={this.props.isSelected}
                    $isGray={isGray}
                    $isTileClickable={isTileClickable}
                    $isDisabled={this.props.isDisabled}>
                    <BackgroundMask>
                        <Header data-testid={TestIds.CompanyTileHeader}>
                            <Avatar src={logoUrl}
                                    size={AvatarSize.XL}
                                    transparent/>
                        </Header>
                    </BackgroundMask>
                    <Content
                        $hasAction={!!this.props.actionIcon}
                        data-testid={TestIds.CompanyTileContent}
                    >
                        <Body data-testid={TestIds.CompanyTileBody}>
                            {isInitialized && !isDeleteRequested &&
                                <LegalNumber title={company.LegalNumber}>
                                    {company.LegalNumber}
                                </LegalNumber>
                            }

                            <BoldLabel title={company.Name}
                                       $isGray={isGray}>
                                {company.Name}
                            </BoldLabel>

                            {(!isInitialized || isArchived) &&
                                <BoldLabel
                                    title={this.props.t(`Common:General.${isArchived ? "Archived" : "Inactive"}`)}
                                    $isGray={isGray}
                                    style={{
                                        marginTop: "12px"
                                    }}>
                                    {this.props.t(`Common:General.${isArchived ? "Archived" : "Inactive"}`)}
                                </BoldLabel>
                            }

                            {!isGray &&
                                <Footer data-testid={TestIds.CompanyTileFooter} title={footerText}>
                                    {footerText}
                                </Footer>
                            }
                            {isDeleteRequested &&
                                <DeleteRequestedText>
                                    {this.props.t("Companies:CompanyTile.WillBeDeletedAfterMailConfirm")}
                                </DeleteRequestedText>
                            }
                        </Body>
                    </Content>
                    {showCustomerPortalButton && (
                        <CompanyTileAvatarButton link={`${ROUTE_SWITCH_TO_CUSTOMER_PORTAL}/${Id}`}
                                                 onClick={this.handleSwitchToCustomerPortal}
                                                 title={this.props.t("Common:General.SwitchToCustomerPortal")}>
                            <CustomerPortalIcon width={IconSize.M} isLight/>
                        </CompanyTileAvatarButton>
                    )}
                </StyledCompanyTile>
            </CompanyTileAndActionWrapper>
        );
    }
}

export default withTheme(withTranslation(["Common", "Components", "Companies"])(CompanyTile));